/**
 * Convert Jira wiki markup to a CommonMark-safe subset.
 *
 * The `description` field returned by Jira's REST/Xray for issues created in
 * the legacy wiki editor (or any project that hasn't migrated to ADF in the
 * UI) is wiki markup, NOT Markdown. The two share `# ` as a token but with
 * incompatible semantics:
 *
 *   - Wiki markup: `# foo`  -> ordered list item, `## foo` nested item
 *   - CommonMark : `# foo`  -> H1 heading,        `## foo` H2 heading
 *
 * Feeding wiki markup straight to ReactMarkdown therefore renders list rows
 * at heading size. This converter normalises the few tokens we care about so
 * the wizard test description displays correctly without losing semantics.
 *
 * The transform is intentionally conservative: it only touches tokens that
 * are unambiguous wiki markup AND would render badly as Markdown, and it
 * leaves anything that already looks like valid Markdown alone.
 */

const NUMBERED_LIST = /^(#+)\s+(\S.*)$/; // `# foo`, `## foo`, ...
const BULLET_LIST = /^(\*+)\s+(\S.*)$/; // `* foo`, `** foo`, ...
const HEADING = /^h([1-6])\.\s+(\S.*)$/i; // `h1. Title`, `h2. Title`
const BLOCKQUOTE = /^bq\.\s+(\S.*)$/i; // `bq. quoted line`
const NOFORMAT_OPEN = /\{noformat(?::[^}]*)?\}/i;
const NOFORMAT_CLOSE = /\{noformat\}/i;
const CODE_OPEN = /\{code(?::([^}]+))?\}/i;
const CODE_CLOSE = /\{code\}/i;
const QUOTE_OPEN = /\{quote\}/i;
const QUOTE_CLOSE = /\{quote\}/i;
const PANEL_OPEN = /\{panel(?::[^}]*)?\}/i;
const PANEL_CLOSE = /\{panel\}/i;

/**
 * Whitelist of characters allowed in a CSS color value embedded into an
 * inline `style` attribute. Anything else gets dropped so we never inject
 * arbitrary CSS expressions from Jira-side content.
 */
const SAFE_COLOR = /^[#a-zA-Z0-9 ,.\-%()/]+$/;

function safeColor(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (!SAFE_COLOR.test(trimmed)) return null;
  return trimmed;
}

/**
 * Inline Jira-wiki -> Markdown/HTML transforms. Applied to a single source
 * line after block-level classification (bullet/numbered/heading) so it
 * never re-fires on tokens that are already block markers. Order matters:
 *
 *   1. Inline code first so its contents are protected from later rules.
 *   2. {color:...}{color} -> <span style="color:...">...</span>. Renders
 *      via `rehype-raw` in `TestDetailPane`.
 *   3. [text|url|...] / [url] link macros -> Markdown links.
 *   4. !image! image macros -> Markdown images.
 *   5. Emphasis tokens (*bold*, _italic_, +underline+, -strike-, ^sup^,
 *      ~sub~). These are the most fragile - we require a word-boundary
 *      style "anchor" character before/after to avoid matching prose like
 *      "5*6" or in-word underscores.
 */
function inlineWiki(line: string): string {
  let out = line;

  out = out.replace(/\{\{([^}\n]+)\}\}/g, '`$1`');

  out = out.replace(/\{color:([^}\n]+)\}([\s\S]*?)\{color\}/gi, (_m, raw, text) => {
    const color = safeColor(String(raw));
    if (!color) return String(text);
    return `<span style="color:${color}">${text}</span>`;
  });

  out = out.replace(
    /\[([^\[\]\n|]+?)\|([^\[\]\n|]+?)(?:\|[^\]\n]*)?\]/g,
    (_m, text, url) => {
      const t = String(text).trim();
      const u = String(url).trim();
      if (!u) return _m;
      return `[${t || u}](${u})`;
    },
  );
  out = out.replace(/\[(https?:\/\/[^\[\]\s|]+)\]/g, '<$1>');

  out = out.replace(/!([^!\n]+\.(?:png|jpg|jpeg|gif|webp|svg))(?:\|[^!\n]*)?!/gi, '![]($1)');

  out = out.replace(
    /(^|[\s({\[>,.!?;:])\*([^\s*][^*\n]*?[^\s*]|[^\s*])\*(?=[\s)}\].,!?;:]|$)/g,
    '$1**$2**',
  );

  out = out.replace(
    /(^|[\s({\[>,.!?;:])_([^\s_][^_\n]*?[^\s_]|[^\s_])_(?=[\s)}\].,!?;:]|$)/g,
    '$1*$2*',
  );

  out = out.replace(
    /(^|[\s({\[>,.!?;:])-([^\s-][^\-\n]*?[^\s-]|[^\s-])-(?=[\s)}\].,!?;:]|$)/g,
    '$1~~$2~~',
  );

  out = out.replace(
    /(^|[\s({\[>,.!?;:])\+([^\s+][^+\n]*?[^\s+]|[^\s+])\+(?=[\s)}\].,!?;:]|$)/g,
    '$1<u>$2</u>',
  );

  out = out.replace(
    /(^|[\s({\[>,.!?;:])\^([^\s^][^\^\n]*?[^\s^]|[^\s^])\^(?=[\s)}\].,!?;:]|$)/g,
    '$1<sup>$2</sup>',
  );

  return out;
}

export function jiraWikiToMarkdown(input: string | null | undefined): string {
  if (!input) return '';

  const src = input.replace(/\r\n/g, '\n');
  const lines = src.split('\n');
  const out: string[] = [];

  let inFence = false;
  let inQuote = false;
  let inPanel = false;

  for (let raw of lines) {
    if (inFence) {
      if (NOFORMAT_CLOSE.test(raw) || CODE_CLOSE.test(raw)) {
        inFence = false;
        out.push('```');
      } else {
        out.push(raw);
      }
      continue;
    }
    if (inQuote) {
      if (QUOTE_CLOSE.test(raw)) {
        inQuote = false;
      } else {
        out.push(`> ${raw}`);
      }
      continue;
    }
    if (inPanel) {
      if (PANEL_CLOSE.test(raw)) {
        inPanel = false;
      } else {
        out.push(`> ${raw}`);
      }
      continue;
    }

    const noFormat = raw.match(NOFORMAT_OPEN);
    if (noFormat) {
      inFence = true;
      out.push('```');
      continue;
    }
    const codeOpen = raw.match(CODE_OPEN);
    if (codeOpen) {
      inFence = true;
      out.push(codeOpen[1] ? '```' + codeOpen[1] : '```');
      continue;
    }
    if (QUOTE_OPEN.test(raw)) {
      inQuote = true;
      continue;
    }
    if (PANEL_OPEN.test(raw)) {
      inPanel = true;
      continue;
    }

    const numbered = raw.match(NUMBERED_LIST);
    if (numbered) {
      const depth = numbered[1].length - 1;
      out.push(`${'    '.repeat(depth)}1. ${inlineWiki(numbered[2])}`);
      continue;
    }

    const bullet = raw.match(BULLET_LIST);
    if (bullet) {
      const depth = bullet[1].length - 1;
      out.push(`${'    '.repeat(depth)}- ${inlineWiki(bullet[2])}`);
      continue;
    }

    const heading = raw.match(HEADING);
    if (heading) {
      const level = Number(heading[1]);
      out.push(`${'#'.repeat(level)} ${inlineWiki(heading[2])}`);
      continue;
    }

    const bq = raw.match(BLOCKQUOTE);
    if (bq) {
      out.push(`> ${inlineWiki(bq[1])}`);
      continue;
    }

    out.push(inlineWiki(raw));
  }

  if (inFence) out.push('```');

  return out.join('\n');
}
